/**
 * Notifications Dialog
 * Displays unread notifications in a dialog with tabs for filtering by type
 * Styled to match other settings dialogs (bottom sheet on mobile, centered on desktop)
 */

import { Link } from 'react-router-dom'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { useNotifications, type Notification } from '@/context/notifications-context'
import { SuccessCircleIcon, ArrowLeft, ReadIcon, ExternalLink } from '@/components/icons'

interface NotificationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Format relative time for notification timestamps
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString()
}

/**
 * Single notification item
 */
function NotificationItem({ notification, onNavigate }: { notification: Notification; onNavigate?: () => void }) {
  const hasLink = notification.notification_type === 'automation_failed' && notification.execution_log_id
  
  return (
    <div className="p-3 border rounded-lg bg-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{notification.title}</p>
          <p className="text-sm text-muted-foreground mt-1 break-words line-clamp-3">
            {notification.message}
          </p>
          {hasLink && (
            <Link 
              to={`/automation-executions?id=${notification.execution_log_id}`}
              onClick={onNavigate}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
            >
              View execution details
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
          {formatRelativeTime(notification.created_at)}
        </span>
      </div>
    </div>
  )
}

/**
 * Empty state when no notifications
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <SuccessCircleIcon className="h-12 w-12 text-green-500 mb-3" />
      <p className="text-sm font-medium text-foreground">All systems operational</p>
      <p className="text-xs text-muted-foreground mt-1">No issues to report</p>
    </div>
  )
}

/**
 * Notification list component
 */
function NotificationList({ notifications, onNavigate }: { notifications: Notification[]; onNavigate?: () => void }) {
  if (notifications.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="space-y-2 overflow-y-auto">
      {notifications.map((notification) => (
        <NotificationItem key={notification.id} notification={notification} onNavigate={onNavigate} />
      ))}
    </div>
  )
}

export function NotificationsDialog({ open, onOpenChange }: NotificationsDialogProps) {
  const { notifications, loading, markAllAsRead } = useNotifications()

  // Filter notifications by type
  const syncNotifications = notifications.filter(n => n.notification_type === 'sync_failed')
  const automationNotifications = notifications.filter(n => n.notification_type === 'automation_failed')

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[70vh] max-h-[70vh]
          md:w-[500px] md:h-auto md:max-h-[60vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogDescription className="sr-only">View and manage your notifications.</DialogDescription>
        <DialogHeader className="px-4 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            {/* Back Button - Icon only on mobile, icon + text on desktop */}
            <Button
              variant="secondary"
              onClick={handleClose}
              className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Back</span>
            </Button>
            
            <DialogTitle className="flex-1 text-center">Notifications</DialogTitle>
            
            {/* Mark all as read - Icon only on mobile, icon + text on desktop */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={notifications.length === 0 || loading}
              className="flex items-center gap-2 md:px-3 px-2"
            >
              <ReadIcon className="h-4 w-4" />
              <span className="hidden md:inline text-xs">Mark all as read</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="px-4 pb-6 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all" className="text-xs">
                  All {notifications.length > 0 && `(${notifications.length})`}
                </TabsTrigger>
                <TabsTrigger value="syncs" className="text-xs">
                  Syncs {syncNotifications.length > 0 && `(${syncNotifications.length})`}
                </TabsTrigger>
                <TabsTrigger value="automations" className="text-xs">
                  Automations {automationNotifications.length > 0 && `(${automationNotifications.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4">
                <NotificationList notifications={notifications} onNavigate={handleClose} />
              </TabsContent>

              <TabsContent value="syncs" className="mt-4">
                <NotificationList notifications={syncNotifications} onNavigate={handleClose} />
              </TabsContent>

              <TabsContent value="automations" className="mt-4">
                <NotificationList notifications={automationNotifications} onNavigate={handleClose} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
